import type { CategoryData } from "@hitit/api"
import { addHours, format } from "date-fns";
import { TranslationDTO } from "../TranslationDTO";
import StoreItem from "./StoreItem";

export default class Category {

    public translationDTOs: Array<{
        id?: string;
        name: string;
        description?: string;
        lang: string;
        defaultLang: boolean;
    }>;
    private id?: string;
    private storeItemDTOs: StoreItem[];
    public enabled: boolean;
    public displaySort: number;
    public taxPercent: number;
    public taxable: boolean;
    public takeoutable: boolean;
    public deliverable: boolean;
    public sellableInStore: boolean;
    public sellableOnline: boolean;
    public preOrderable: boolean;
    public validFromDate: string;
    public validUntil?: string;
    public rewardable: boolean;

    static convinienceConstructor(name: string, displaySort: number) {
        const translation = new TranslationDTO(name);
        const offset = new Date().getTimezoneOffset() / 60;
        const validFromDate = format(addHours(new Date(), offset), "yyyy-MM-dd|HH:mm:ss"
        ).replace("|", "T");
        const categoryData: CategoryData = {
            translationDTOs: [translation],
            id: `|TEMP|${Math.random()}`,
            enabled: true,
            displaySort: displaySort,
            taxPercent: 10,
            taxable: true,
            takeoutable: false,
            deliverable: false,
            sellableInStore: false,
            sellableOnline: false,
            preOrderable: false,
            validFromDate: validFromDate,
            validUntil: undefined,
            rewardable: false,
            storeItemDTOs: []
        }

        return new Category(categoryData)
    }


    constructor(data: CategoryData) {
        this.translationDTOs = data.translationDTOs;
        this.id = data.id;
        this.enabled = data.enabled;
        this.displaySort = data.displaySort;
        this.taxPercent = data.taxPercent;
        this.taxable = data.taxable;
        this.takeoutable = data.takeoutable;
        this.deliverable = data.deliverable;
        this.sellableInStore = data.sellableInStore
        this.preOrderable = data.preOrderable
        this.validFromDate = data.validFromDate
        this.validUntil = data.validUntil
        this.rewardable = data.rewardable
        this.sellableOnline = data.sellableOnline
        this.storeItemDTOs = data.storeItemDTOs.map((v) => new StoreItem(v, this)).sort((a, b) => a.displaySort - b.displaySort)
    }

    public getStoreItems() {
        return [...this.storeItemDTOs]
    }

    public getStoreItemLength() {
        return this.storeItemDTOs.length;
    }

    public setStoreItem(storeItem: StoreItem) {
        this.storeItemDTOs = [...this.storeItemDTOs.filter(v => v.id !== storeItem.id), storeItem]
            .sort((a, b) => a.displaySort - b.displaySort)
    }

    public replaceStoreItemByUid(storeItem: StoreItem, uid: string) {
        this.storeItemDTOs = [...this.storeItemDTOs.filter(v => v.uid !== uid), storeItem]
            .sort((a, b) => a.displaySort - b.displaySort)
    }

    public getId() {
        return this.id
    }

    public getTranslations() {
        return this.translationDTOs.map((v) => {
            const { name, id, description, defaultLang, lang } = v;
            return new TranslationDTO(name, lang, defaultLang, id, description)
        })
    }

    public setTranslations(translations: TranslationDTO[]) {
        this.translationDTOs = [...translations]
    }

    public getLastDisplaysort() {
        return this.storeItemDTOs.reduce((prev, cur) => {
            if (prev < cur.displaySort) {
                return cur.displaySort
            }
            return prev
        }, -1)
    }

    public deleteItem(id: string) {
        this.storeItemDTOs = this.storeItemDTOs.filter((v) => v.id !== id);
    }

    public deleteItemByUid(uid: string) {
        this.storeItemDTOs = this.storeItemDTOs.filter((v) => v.uid !== uid);
    }

    public getCategoryDTO() {
        const reg = /^\|TEMP\|/;
        const dto: CategoryData = {
            translationDTOs: this.translationDTOs,
            id: reg.test(this.id!) ? undefined : this.id,
            enabled: this.enabled,
            displaySort: this.displaySort,
            taxPercent: this.taxPercent,
            taxable: this.taxable,
            takeoutable: this.takeoutable,
            deliverable: this.deliverable,
            sellableInStore: this.sellableInStore,
            sellableOnline: this.sellableOnline,
            preOrderable: this.preOrderable,
            validFromDate: this.validFromDate,
            validUntil: this.validUntil,
            rewardable: this.rewardable,
            storeItemDTOs: this.storeItemDTOs.map((v) => v.getStoreItemDTO())
        }
        return dto
    }

    public sortItem() {
        return this.storeItemDTOs.sort((a, b) => a.displaySort - b.displaySort)
            .map((v) => {
                v.sortOptionCategory();
                return v
            })
    }

    public findItemByUid(uid: string) {
        return this.storeItemDTOs.find((v) => v.uid === uid)
    }

    public findItemById(id?: string) {
        return this.storeItemDTOs.find((v) => v.id === id)
    }

    public updateDisplaysort(oldIndex: number, newIndex: number) {

        if (this.storeItemDTOs[oldIndex] === undefined) {
            return this.storeItemDTOs.map((v, i) => ({
                id: v.id || '',
                newDisplaySort: i
            }))
        }
        if (oldIndex < newIndex) {
            this.storeItemDTOs.splice(newIndex + 1, 0, this.storeItemDTOs[oldIndex]);
            this.storeItemDTOs.splice(oldIndex, 1);
        } else {
            this.storeItemDTOs.splice(newIndex, 0, this.storeItemDTOs[oldIndex]);
            this.storeItemDTOs.splice(oldIndex + 1, 1);
        }

        const updatedItems: { id: string, newDisplaySort: number }[] = [];

        for (let i = 0; i < this.storeItemDTOs.length; i++) {
            const target = this.storeItemDTOs[i];
            const updatedItem = {
                id: target.getId() || "",
                newDisplaySort: i
            }

            updatedItems.push(updatedItem);
            target.displaySort = i;
        }
        this.sortItem()
        return updatedItems
    }
    public updateMultipleDisplaySort(oldIndexes: number[], newIndexes: number[]) {
        const moveItem = this.storeItemDTOs.filter(
            (_, i) => oldIndexes.find((v) => v === i) !== undefined
        )
        const filtered = this.storeItemDTOs.filter((_, i) =>
            oldIndexes.find((v) => v === i) === undefined
        )

        filtered.splice(newIndexes[0], 0, ...moveItem);
        this.storeItemDTOs = filtered;
        const updatedItems: { id: string, newDisplaySort: number }[] = [];

        for (let i = 0; i < this.storeItemDTOs.length; i++) {
            const target = this.storeItemDTOs[i];
            const updatedItem = {
                id: target.getId() || "",
                newDisplaySort: i
            }


            target.displaySort = i;
            updatedItems.push(updatedItem)
        }
        this.sortItem()

        return updatedItems
    }
}